import { RefObject, memo, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ControlledTextInput } from "@/components/FormInputs";
import { AccessDenied, Protect } from "@/components/Protect";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { FragmentType, getFragmentData, graphql } from "@/gen";
import useDebounce from "@/hooks/useDebounce";
import { Permissions } from "@/hooks/usePermissions";
import { useSearchPlayers } from "@/hooks/useSearchPlayers";
import { Player } from "@/routes/players/components/Player";

const PlayerRow_Player = graphql(`
    fragment PlayerRow_Player on Player {
        id
        name
        fideId
    }
`);

const PlayerRow = memo(
    ({ playerFragment }: { playerFragment: FragmentType<typeof PlayerRow_Player> }) => {
        const navigate = useNavigate();
        const player = getFragmentData(PlayerRow_Player, playerFragment);
        const params = usePlayersSearchParams();

        const urlSearchParams = new URLSearchParams();

        if (params.query) {
            urlSearchParams.set("q", params.query);
        }

        if (params.pageNumber) {
            urlSearchParams.set("page", params.pageNumber.toString());
        }

        return (
            <TableRow
                key={player.id}
                onClick={() => navigate(`/players/${player.id}?${urlSearchParams.toString()}`)}
            >
                <TableCell className="font-medium">{player.name}</TableCell>

                <TableCell className="font-medium">
                    {player.fideId ? <Badge variant="secondary">{player.fideId}</Badge> : "-"}
                </TableCell>
            </TableRow>
        );
    }
);

const usePlayersSearchParams = () => {
    const [params, setParams] = useSearchParams();

    const updateParam = useCallback(
        (key: string, value?: string, replace?: boolean) => {
            if (value) {
                params.set(key, value);
            } else {
                params.delete(key);
            }
            setParams(params, { replace });
        },
        [params, setParams]
    );

    return {
        query: params.get("q") ?? "",
        setQuery: useCallback(
            (query: string) => {
                updateParam("q", query, true);
                updateParam("page", undefined);
            },
            [updateParam]
        ),

        pageNumber: parseInt(params.get("page") ?? "0", 10),
        setPageNumber: useCallback(
            (page: number) => updateParam("page", page.toString(), true),
            [updateParam]
        ),

        selectedPlayerId: params.get("player-id"),
        setSelectedPlayerId: useCallback(
            (id: string) => updateParam("player-id", id),
            [updateParam]
        ),
    };
};

const PlayersRouteContent = () => {
    const params = usePlayersSearchParams();

    const { id } = useParams();

    const debouncedQuery = useDebounce(params.query, 300);

    const { players, loading, error, hasMore, playersCount } = useSearchPlayers({
        query: debouncedQuery,
        currentPage: params.pageNumber,
    });

    const playersListRef = useRef<HTMLDivElement>(null);

    const scrollToTop = (ref: RefObject<HTMLDivElement>) => {
        ref.current?.scroll({
            top: 0,
        });
    };

    if (loading && !players) {
        return <p>Loading...</p>;
    }

    if (error) {
        return <p>Error: {error.message}</p>;
    }

    const prevButtonDisabled = loading || params.pageNumber === 0;
    const nextButtonDisabled = loading || !hasMore;
    return (
        <div className="flex h-screen w-full flex-1 overflow-hidden">
            <div className="w-1/3">
                <Card className="flex h-full flex-col">
                    <CardHeader>
                        <CardTitle>Players</CardTitle>
                        <div className="px-2 py-4">
                            <ControlledTextInput
                                value={params.query}
                                id="players"
                                onValue={val => params.setQuery(val)}
                                label="Search for players"
                                placeholder="Search"
                            />
                        </div>
                        <div className="px-2 py-4 text-sm font-semibold">
                            {playersCount ? (
                                <p>Found {playersCount} players</p>
                            ) : (
                                <p>No players found</p>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent className="overflow-y-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Player</TableHead>
                                    <TableHead>Fide-id</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {players.map(player => {
                                    return <PlayerRow key={player.id} playerFragment={player} />;
                                })}
                            </TableBody>
                        </Table>
                    </CardContent>

                    <Pagination className="my-4">
                        <PaginationContent>
                            {!prevButtonDisabled && (
                                <>
                                    <PaginationItem
                                        onClick={() => {
                                            scrollToTop(playersListRef);
                                            params.setPageNumber(
                                                Math.max(0, params.pageNumber - 1)
                                            );
                                        }}
                                    >
                                        <PaginationPrevious />
                                    </PaginationItem>

                                    <PaginationItem
                                        onClick={() => {
                                            scrollToTop(playersListRef);
                                            params.setPageNumber(
                                                Math.max(0, params.pageNumber - 1)
                                            );
                                        }}
                                    >
                                        <PaginationLink>{params.pageNumber}</PaginationLink>
                                    </PaginationItem>
                                </>
                            )}
                            <PaginationItem>
                                <PaginationLink href="#" isActive>
                                    {params.pageNumber + 1}
                                </PaginationLink>
                            </PaginationItem>
                            <PaginationItem
                                onClick={() => {
                                    scrollToTop(playersListRef);
                                    params.setPageNumber(params.pageNumber + 1);
                                }}
                            >
                                {!nextButtonDisabled && (
                                    <PaginationLink href="#">
                                        {params.pageNumber + 2}
                                    </PaginationLink>
                                )}
                            </PaginationItem>
                            {!nextButtonDisabled && (
                                <>
                                    <PaginationItem>
                                        <PaginationEllipsis />
                                    </PaginationItem>
                                    <PaginationItem
                                        onClick={() => {
                                            scrollToTop(playersListRef);
                                            params.setPageNumber(params.pageNumber + 1);
                                        }}
                                    >
                                        <PaginationNext />
                                    </PaginationItem>
                                </>
                            )}
                        </PaginationContent>
                    </Pagination>
                </Card>
            </div>

            <div className="bg-background h-full w-2/3 overflow-y-auto">
                <div className="px-8">{id && <Player key={id} id={id} />}</div>
            </div>
        </div>
    );
};

const PlayersRoute = () => {
    return (
        <Protect
            permission={Permissions.PLAYER_MANAGEMENT}
            fallback={
                <div className="flex h-screen items-center justify-center">
                    <AccessDenied message="You don't have permission to manage players" />
                </div>
            }
        >
            <PlayersRouteContent />
        </Protect>
    );
};

export default PlayersRoute;
